import { useEffect, useMemo, useState } from 'react';
import { buscarErrosDaClinica, ouvirClinicas } from '@/services/adminService';
import { ouvirUsuariosDaClinica } from '@/services/usuariosService';
import { agruparPorTipo, contarNaJanela } from '@/produtividade/saude';
import AvisoCarga from '@/components/AvisoCarga';

/**
 * ★★★ WT lote 1 — SAÚDE DO SISTEMA (só superadmin).
 *
 * ⚠⚠ Até agora **nenhum erro do navegador saía do navegador** (`docs/wt-medicao.md` §9): o
 * caso da Jéssica só apareceu quando ela reclamou, e o defeito da WC durou sete semanas
 * porque a recusa de permissão virava ausência silenciosa. ★ Esta tela é o outro lado do
 * registro: ela existe para que **ninguém precise reclamar**.
 *
 * ★★ SÓ LEITURA, de propósito — nenhum botão de apagar. ⚠ Registro que alguém pode apagar
 * não diagnostica nada, e as rules já negam `delete` a todos, inclusive ao superadmin.
 *
 * ★ Agrupa por `codigo` + `tela` + `origem`, a MESMA chave do dedup de 60 s. A conta mora em
 * `produtividade/saude.js` (puro); aqui é só desenho.
 */
const JANELAS = [
  { id: '24h', rotulo: 'Últimas 24 h', horas: 24 },
  { id: '7d', rotulo: 'Últimos 7 dias', horas: 24 * 7 },
];

const hora = (ms) => (ms ? new Date(ms).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');

export default function SaudePage() {
  const [clinicas, setClinicas] = useState([]);
  const [clinicaId, setClinicaId] = useState('');
  const [erros, setErros] = useState(null);
  const [equipe, setEquipe] = useState([]);
  const [falhaLista, setFalhaLista] = useState(false);
  const [falhaErros, setFalhaErros] = useState(false);
  const [aberto, setAberto] = useState(null);
  // ★ O relógio é lido UMA vez por carga, e injetado na conta pura (regra da WN).
  const [agora, setAgora] = useState(() => Date.now());

  useEffect(() => ouvirClinicas(setClinicas, () => setFalhaLista(true)), []);

  useEffect(() => {
    if (!clinicaId) { setErros(null); return undefined; }
    return ouvirUsuariosDaClinica(clinicaId, setEquipe, () => setEquipe([]));
  }, [clinicaId]);

  useEffect(() => {
    if (!clinicaId) return;
    let cancelado = false;
    setErros(null);
    setFalhaErros(false);
    setAgora(Date.now());
    buscarErrosDaClinica(clinicaId)
      .then((lista) => { if (!cancelado) setErros(lista); })
      .catch(() => { if (!cancelado) { setErros([]); setFalhaErros(true); } });
    // eslint-disable-next-line consistent-return
    return () => { cancelado = true; };
  }, [clinicaId]);

  const nomePorUid = useMemo(
    () => new Map(equipe.map((u) => [u.id, u.nome ?? u.id])),
    [equipe],
  );
  /**
   * ★★★ WT lote 1b — DUAS SEÇÕES POR JANELA: sistema primeiro, uso depois.
   *
   * ⚠ Nasceu do primeiro registro real (um `invalid-argument` de digitação): com dez
   * clínicas, o erro de uso **soterra** o de sistema. ★ A ordem é fixa de propósito.
   */
  const porJanela = useMemo(
    () => JANELAS.map((j) => ({
      ...j,
      total: contarNaJanela(erros ?? [], agora, j.horas),
      tipos: agruparPorTipo(erros ?? [], agora, j.horas),
    })),
    [erros, agora],
  );

  return (
    <div className="pagina">
      <header className="pagina-cabecalho">
        <h1>Saúde do sistema</h1>
        <p className="painel-sub">
          Erros que o navegador da equipe registrou. Só leitura — nada aqui apaga nada.
        </p>
        {falhaLista && <AvisoCarga oQue="a lista de clínicas" />}
      </header>

      <div className="campo">
        <label htmlFor="saude-clinica">Clínica</label>
        <select id="saude-clinica" value={clinicaId} onChange={(e) => setClinicaId(e.target.value)}>
          <option value="">Escolha uma clínica…</option>
          {clinicas.map((c) => <option key={c.id} value={c.id}>{c.nome ?? c.id}</option>)}
        </select>
      </div>

      {!clinicaId && <p className="vazio">Escolha uma clínica para ver os erros dela.</p>}
      {clinicaId && falhaErros && <AvisoCarga oQue="os erros desta clínica" />}
      {clinicaId && erros === null && !falhaErros && <p className="vazio">Carregando…</p>}

      {clinicaId && erros !== null && !falhaErros && porJanela.flatMap((j) => j.tipos.map((t) => (
        <section className={`secao saude-${t.id}`} key={`${j.id}:${t.id}`}>
          <header className="secao-barra">
            <div>
              <h2>{t.rotulo} · {j.rotulo}</h2>
              <p className="painel-sub">
                <strong>{t.total}</strong> erro(s) em <strong>{t.grupos.length}</strong> grupo(s)
                {t.id === 'uso' && (
                  /* ★ Diz o que a seção É, senão "erro de uso" parece defeito menor. */
                  <> — ★ entrada recusada pelo servidor; <strong>não é defeito do sistema</strong></>
                )}
              </p>
            </div>
          </header>
          {t.grupos.length === 0 ? (
            /* ★ "nenhum erro" aqui é NOTÍCIA BOA, e é diferente de "não carregou" — que
               aparece como AvisoCarga acima. */
            <p className="vazio">Nenhum erro registrado nesta janela.</p>
          ) : (
            <div className="tabela-envolve">
              <table className="tabela">
                <thead>
                  <tr>
                    {/* ★★★ WU-2/P1 — a coluna ASSUNTO: é ela que diz O QUE não carregou.
                        ⚠ Registro de antes do deploy mostra `—`, e isso é fiel: o assunto
                        dele foi descartado na gravação e não há como recuperá-lo. */}
                    <th>Código</th><th>Assunto</th><th>Tela</th><th>Origem</th>
                    <th>Qtd.</th><th>Último</th><th aria-label="abrir" />
                  </tr>
                </thead>
                <tbody>
                  {t.grupos.map((g) => {
                    const id = `${j.id}:${t.id}:${g.chave}`;
                    return (
                      <Celulas key={id}>
                        <tr>
                          <td><code>{g.codigo}</code></td>
                          <td>{g.assunto}</td>
                          <td>{g.tela}</td>
                          <td>{g.origem}</td>
                          <td><strong>{g.quantidade}</strong></td>
                          <td>{hora(g.ultimoEm)}</td>
                          <td>
                            <button
                              type="button"
                              className="link"
                              aria-expanded={aberto === id}
                              onClick={() => setAberto(aberto === id ? null : id)}
                            >
                              {aberto === id ? 'fechar' : `ver ${Math.min(g.itens.length, 20)}`}
                            </button>
                          </td>
                        </tr>
                        {aberto === id && (
                          <tr className="detalhe">
                            <td colSpan={7}>
                              <ul className="lista-erros">
                                {g.itens.map((e) => (
                                  <li key={e.id}>
                                    <span className="quando">{hora(e.quandoMs)}</span>
                                    {' · '}
                                    <span className="quem">{nomePorUid.get(e.uid) ?? e.uid}</span>
                                    {' · '}
                                    <span className="bundle">{e.bundle ?? 'sem bundle'}</span>
                                    {' · '}
                                    <span className="navegador">{e.navegador ?? '—'}</span>
                                    <br />
                                    <span className="mensagem">{e.mensagem}</span>
                                  </li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        )}
                      </Celulas>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )))}
    </div>
  );
}

/** Fragmento com chave — mantém o JSX legível com duas linhas por grupo. */
function Celulas({ children }) {
  return <>{children}</>;
}
